<template>
  <div class="stat-stacked-chart">
    <div class="chart-head">
      <div class="chart-title">{{ title }}</div>
      <slot name="controls" />
    </div>
    <VChart
      v-if="rows.length"
      class="chart"
      :option="option"
      :style="{ height: height + 'px' }"
      autoresize
      @legendselectchanged="$emit('legendselectchanged', $event)"
    />
    <div v-else class="chart-empty" :style="{ height: height + 'px' }">{{ emptyText }}</div>
  </div>
</template>

<script>
import { computed } from 'vue';
import VChart from '@/components/charts/echarts';
import { DOWNTIME_STATUSES, downtimeStatusMeta } from '../downtimeStatuses.mjs';

export default {
  name: 'StatStackedBarChart',
  components: { VChart },
  props: {
    title: { type: String, default: '' },
    // rows: [{ label, values: { confirmed: n, potential: n, ... } }]
    rows: { type: Array, default: () => [] },
    height: { type: Number, default: 320 },
    emptyText: { type: String, default: 'Нет данных' },
    valueFormatter: { type: Function, default: (v) => v },
    // Явная полная карта видимости статусов ({ label: bool }); null = все вкл.
    legendSelected: { type: Object, default: null },
  },
  emits: ['legendselectchanged'],
  setup(props) {
    const option = computed(() => ({
      grid: { left: 8, right: 56, top: 28, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        confine: true,
        formatter: (params) => {
          if (!Array.isArray(params) || !params.length) return '';
          let total = 0;
          const lines = params
            .filter((item) => item.value > 0)
            .map((item) => {
              total += item.value;
              return `${item.marker} ${item.seriesName}: <b>${props.valueFormatter(item.value)}</b>`;
            });
          const head = `<b>${params[0].name}</b>`;
          const sum = `Итого: <b>${props.valueFormatter(total)}</b>`;
          return [head, ...lines, sum].join('<br/>');
        },
      },
      legend: {
        top: 0,
        type: 'scroll',
        textStyle: { fontSize: 11, color: '#667085' },
        ...(props.legendSelected ? { selected: props.legendSelected } : {}),
      },
      xAxis: {
        type: 'value',
        axisLabel: { formatter: (v) => props.valueFormatter(v) },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: props.rows.map((row) => row.label),
        axisLabel: {
          width: 190,
          overflow: 'truncate',
          fontSize: 12,
          color: '#475467',
        },
      },
      series: DOWNTIME_STATUSES.map((status) => ({
        name: status.label,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 22,
        itemStyle: { color: status.chartColor },
        data: props.rows.map((row) => Number(row.values?.[status.value]) || 0),
      })),
    }));

    return { option, downtimeStatusMeta };
  },
};
</script>

<style scoped>
.stat-stacked-chart {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
}

.chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.chart {
  width: 100%;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
